/**
 * SavedViews.jsx — per-user saved views for the All Cases list (saved_views_logic.md).
 *
 * Renders the page TITLE (the active view's name, or "All Cases" on the default) with
 * a star + chevron beside it. When the list is customised a STAR appears; clicking it
 * saves the current filters + columns + sort (name + optional default). Once ≥1 view
 * is saved, a CHEVRON dropdown lists "All Cases" (the built-in default) then the user's
 * views (active one ticked), ending in "Edit views…" (rename / set default / delete).
 * Views are PER USER — this only ever reads and writes the caller's own.
 *
 * Rendered only for non-clinic users (clinic staff see just their own clinic).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { listSavedViews, createSavedView, updateSavedView, deleteSavedView } from '../api/savedViews.js';

const StarIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const hasUrlParams = () => { try { return !!window.location.search && window.location.search.replace(/^\?/, '').length > 0; } catch { return false; } };
// Keep view names tidy: the first character is always upper-case as you type.
const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function SavedViews({
  defaultLabel = 'All Cases', isCustomized, getCurrentView,
  onApply, onResetDefault, onSaved, onCountChange, applyDefaultOnLoad = true,
}) {
  const [views, setViews] = useState([]);
  const [activeId, setActiveId] = useState(null);   // null = the built-in "All Cases"
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState(null);        // 'save' | 'edit'
  const [name, setName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);
  const appliedDefaultRef = useRef(false);

  const refresh = useCallback(async () => {
    try { const v = await listSavedViews(); setViews(v); onCountChange?.(v.length); return v; }
    catch { return []; }
  }, [onCountChange]);

  // Load the caller's views; apply their default once, only on a fresh visit
  // (no filters already in the URL — a shared deep link wins over the default).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const v = await refresh();
      if (cancelled) return;
      if (applyDefaultOnLoad && !appliedDefaultRef.current && !hasUrlParams()) {
        const def = v.find(x => x.isDefault);
        if (def?.view) { appliedDefaultRef.current = true; onApply(def.view); setActiveId(def.id); }
      }
    })();
    return () => { cancelled = true; };
  }, [refresh, applyDefaultOnLoad, onApply]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  // If the active view is deleted, fall back to the built-in default AND load its
  // filters — otherwise the title says "All Cases" while the deleted view's filters
  // are still applied.
  useEffect(() => {
    if (activeId != null && !views.some(v => v.id === activeId)) {
      setActiveId(null);
      onResetDefault?.();
    }
  }, [views, activeId, onResetDefault]);

  const activeView = views.find(v => v.id === activeId) || null;
  const title = activeView ? activeView.name : defaultLabel;

  const openSave = () => { setName(''); setMakeDefault(false); setError(null); setDialog('save'); };

  const submitSave = async () => {
    const nm = name.trim();
    if (!nm) { setError('Give the view a name.'); return; }
    setBusy(true); setError(null);
    try {
      const saved = await createSavedView({ name: nm, isDefault: makeDefault, view: getCurrentView() });
      await refresh();
      if (saved?.id) setActiveId(saved.id);   // the title becomes the saved name
      onSaved?.();                            // parent clears the "unsaved" star
      setDialog(null);
    } catch (e) { setError(e.message || 'Could not save the view.'); }
    finally { setBusy(false); }
  };

  const pickView = (v) => { setMenuOpen(false); onApply(v.view); setActiveId(v.id); };
  const pickDefault = () => { setMenuOpen(false); onResetDefault?.(); setActiveId(null); };

  return (
    <div className="saved-views" ref={wrapRef}>
      <h1 className="cl-title">{title}</h1>

      {isCustomized && (
        <button type="button" className="sv-star" title="Save this view" aria-label="Save this view" onClick={openSave}>
          <StarIcon />
        </button>
      )}

      {views.length > 0 && (
        <div className="sv-menu-wrap">
          <button type="button" className="sv-chevron" aria-haspopup="menu" aria-expanded={menuOpen}
            title="Your saved views" aria-label="Your saved views" onClick={() => setMenuOpen(o => !o)}>
            <ChevronIcon />
          </button>
          {menuOpen && (
            <div className="sv-menu" role="menu">
              <button type="button" className="sv-menu-item" role="menuitem" onClick={pickDefault}>
                <span className="sv-tick">{activeId == null && <CheckIcon />}</span>
                <span className="sv-name">{defaultLabel}</span>
              </button>
              {views.length > 0 && <div className="sv-menu-sep" />}
              {views.map(v => (
                <button key={v.id} type="button" className="sv-menu-item" role="menuitem" onClick={() => pickView(v)}>
                  <span className="sv-tick">{activeId === v.id && <CheckIcon />}</span>
                  <span className="sv-name">{v.name}</span>
                  {v.isDefault && <span className="sv-default-tag">Default</span>}
                </button>
              ))}
              <div className="sv-menu-sep" />
              <button type="button" className="sv-menu-item sv-edit" role="menuitem"
                onClick={() => { setMenuOpen(false); setError(null); setDialog('edit'); }}>
                Edit views…
              </button>
            </div>
          )}
        </div>
      )}

      {dialog === 'save' && createPortal(
        <SaveDialog
          name={name} setName={setName} makeDefault={makeDefault} setMakeDefault={setMakeDefault}
          existingNames={views.map(v => String(v.name).trim().toLowerCase())}
          busy={busy} error={error} onCancel={() => setDialog(null)} onSave={submitSave}
        />, document.body)}

      {dialog === 'edit' && createPortal(
        <EditDialog views={views} refresh={refresh} onClose={() => setDialog(null)} />, document.body)}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="sv-backdrop" onMouseDown={onClose}>
      <div className="sv-dialog" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.stopPropagation()}>
        <div className="sv-dialog-head">{title}</div>
        {children}
      </div>
    </div>
  );
}

function SaveDialog({ name, setName, makeDefault, setMakeDefault, existingNames = [], busy, error, onCancel, onSave }) {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const trimmed = name.trim();
  const taken = !!trimmed && existingNames.includes(trimmed.toLowerCase());
  const msg = taken ? 'You already have a view with that name.' : error;
  return (
    <Modal title="Save view" onClose={onCancel}>
      <p className="sv-dialog-sub">Saves your current filters, columns and sort. Views are private to you.</p>
      <label className="sv-field">
        <span>Name</span>
        <input ref={inputRef} type="text" maxLength={120} value={name}
          placeholder="e.g. My open Code 4s"
          onChange={(e) => setName(capFirst(e.target.value))}
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy && !taken) onSave(); }} />
      </label>
      <label className="sv-check">
        <input type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />
        <span>Make this my default view</span>
      </label>
      {msg && <div className="sv-error">{msg}</div>}
      <div className="sv-dialog-actions">
        <button type="button" className="btn ghost sm" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn primary sm" onClick={onSave} disabled={busy || taken}>{busy ? 'Saving…' : 'Save view'}</button>
      </div>
    </Modal>
  );
}

function EditDialog({ views, refresh, onClose }) {
  const [rows, setRows] = useState(views);
  const [confirmId, setConfirmId] = useState(null);   // two-step delete (no browser confirm — it can be suppressed)
  const [error, setError] = useState(null);
  useEffect(() => { setRows(views); }, [views]);

  const act = async (fn) => { setError(null); try { await fn(); await refresh(); } catch (e) { setError(e.message || 'Could not update.'); } };
  const rename = (v, value) => setRows(rs => rs.map(r => (r.id === v.id ? { ...r, name: value } : r)));
  const commitRename = (v) => { const nm = (rows.find(r => r.id === v.id)?.name || '').trim(); if (nm && nm !== v.name) act(() => updateSavedView(v.id, { name: nm })); };

  return (
    <Modal title="Edit views" onClose={onClose}>
      {rows.length === 0 && <p className="sv-dialog-sub">No saved views.</p>}
      <ul className="sv-edit-list">
        {rows.map(v => (
          <li key={v.id} className="sv-edit-row">
            <input type="text" className="sv-edit-name" maxLength={120} value={v.name}
              onChange={(e) => rename(v, capFirst(e.target.value))} onBlur={() => commitRename(v)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
            <button type="button" className={`sv-def-btn${v.isDefault ? ' on' : ''}`}
              title={v.isDefault ? 'Clear default' : 'Make default'}
              onClick={() => act(() => updateSavedView(v.id, { isDefault: !v.isDefault }))}>
              {v.isDefault ? 'Default' : 'Set default'}
            </button>
            {confirmId === v.id ? (
              <span className="sv-confirm">
                <button type="button" className="sv-del-yes" onClick={() => act(() => deleteSavedView(v.id))}>Delete</button>
                <button type="button" className="sv-del-no" onClick={() => setConfirmId(null)}>Cancel</button>
              </span>
            ) : (
              <button type="button" className="sv-del" title="Delete view" aria-label={`Delete ${v.name}`} onClick={() => setConfirmId(v.id)}>×</button>
            )}
          </li>
        ))}
      </ul>
      {error && <div className="sv-error">{error}</div>}
      <div className="sv-dialog-actions">
        <button type="button" className="btn primary sm" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}
